import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import ReactCountryFlag from 'react-country-flag';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useCountryActions from '../../../hooks/APIHooks/users/useCountryActions';
import { editCountrySchema } from '../../../schemas';
import { Country, CountryReqBody } from '../../../types/countries';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const EditCountryModal = ({
  country,
  handleModalClose,
}: {
  country: Country;
  handleModalClose: () => void;
}) => {
  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { updateCountryByIdHandler } = useCountryActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to update country declared here
  const updateCountryHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body: CountryReqBody = {
        isd_code: values.isd_code,
        iso_code: values.iso_code,
        updated_by: currentUser?.username,
      };

      await updateCountryByIdHandler
        .mutateAsync({ countryId: country.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Country Updated!!');

            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['country-list'] });
          } else {
            toast.error(res.message || 'Country Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering edit country modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='update country' />

      <Formik
        initialValues={{
          iso_code: country.iso_code || '',
          isd_code: country.isd_code || '',
        }}
        validationSchema={editCountrySchema}
        onSubmit={updateCountryHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <div>
              <label
                htmlFor={`input-country_name`}
                className='block mb-1 text-legal-dark-blue font-medium text-base'
              >
                {'Country Name'} <span className='text-base-red'>*</span>
              </label>

              <div className='flex items-center gap-2 border-2 border-legal-dark-blue rounded-md w-full p-3 text-legal-dark-blue font-semibold text-base disabled:opacity-40 disabled:cursor-not-allowed read-only:opacity-60'>
                <ReactCountryFlag
                  style={{ width: '1.7em', height: '1.42em' }}
                  countryCode={country.iso_code}
                  svg
                />

                {country.country_name}
              </div>
            </div>

            <AcademiaInputField
              type='text'
              name='iso_code'
              label='ISO Code'
              placeholder='Enter ISO Code here...'
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='isd_code'
              label='ISD Code'
              placeholder='Enter ISO Code here...'
              isRequired
            />

            <AcademiaButton
              //   title={
              //     props.values.province.id === -1 || !props.values.city
              //       ? 'Please, select a province & add a city first!'
              //       : ''
              //   }
              type='submit'
              variant='premium'
              icon={<RxUpdate className='size-6' />}
              isLoading={props.isSubmitting}
              //   isDisabled={props.values.province.id === -1 || !props.values.city}
              fullWidth
            >
              Update Country
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default EditCountryModal;
